import React, { useMemo, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, PlusCircleIcon, Share2Icon } from 'lucide-react';
import { addMonths, addYears, eachDayOfInterval, endOfMonth, endOfWeek, format, isSameMonth, isToday, parseISO, startOfMonth, startOfWeek } from 'date-fns';
import { es } from 'date-fns/locale';
import { useJournal } from '../../context/JournalContext';
import { useDisplayMode } from '../../context/DisplayModeContext';
import { ReviewStatus } from '../../lib/types';
import { summarizeByDay, totalOf } from '../../lib/calc';
import { DayCell } from './DayCell';
import { WeekTotalCell } from './WeekTotalCell';
import { cn } from '../../lib/cn';
const WEEKDAYS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
type ReviewFilter = 'all' | 'pending' | 'reviewed' | 'unmarked';
type PlanFilter = 'all' | 'followed' | 'broken';
const GRID_STYLE: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(7, minmax(0, 1fr)) minmax(155px, 0.95fr)',
  columnGap: 3,
  rowGap: 3,
  width: '100%'
};
export function MonthCalendar({
  selected,
  onSelect
}: {
  selected: string | null;
  onSelect: (date: string) => void;
}) {
  const {
    state,
    activeOperations,
    activeJournals
  } = useJournal();
  const {
    mode,
    setMode,
    formatResult
  } = useDisplayMode();
  const [cursor, setCursor] = useState(() => new Date());
  const [shared, setShared] = useState(false);
  const [reviewFilter, setReviewFilter] = useState<ReviewFilter>('all');
  const [planFilter, setPlanFilter] = useState<PlanFilter>('all');
  const reviewByDate = useMemo(() => {
    const map: Record<string, ReviewStatus> = {};
    activeJournals.forEach(journal => {
      map[journal.date] = journal.reviewStatus ?? 'none';
    });
    return map;
  }, [activeJournals]);

  // Filters narrow which operations feed the cells and the totals.
  const filteredOperations = useMemo(() => activeOperations.filter(op => {
    if (planFilter === 'followed' && op.followedPlan !== true) return false;
    if (planFilter === 'broken' && op.followedPlan !== false) return false;
    if (reviewFilter !== 'all') {
      const status = reviewByDate[op.date] ?? 'none';
      if (reviewFilter === 'pending' && status !== 'pending') return false;
      if (reviewFilter === 'reviewed' && status !== 'reviewed') return false;
      if (reviewFilter === 'unmarked' && status !== 'none') return false;
    }
    return true;
  }), [activeOperations, planFilter, reviewFilter, reviewByDate]);
  React.useEffect(() => {
    if (!shared) return;
    const timer = window.setTimeout(() => setShared(false), 1800);
    return () => window.clearTimeout(timer);
  }, [shared]);
  const summaries = useMemo(() => summarizeByDay(filteredOperations), [filteredOperations]);
  const weeks = useMemo(() => {
    const start = startOfWeek(startOfMonth(cursor), {
      weekStartsOn: 1
    });
    const end = endOfWeek(endOfMonth(cursor), {
      weekStartsOn: 1
    });
    const all = eachDayOfInterval({
      start,
      end
    });
    const rows: Date[][] = [];
    for (let i = 0; i < all.length; i += 7) rows.push(all.slice(i, i + 7));
    return rows;
  }, [cursor]);
  const monthKey = format(cursor, 'yyyy-MM');
  const monthOps = filteredOperations.filter(op => op.date.startsWith(monthKey));
  const monthTotal = totalOf(monthOps);
  const monthJournals = activeJournals.filter(journal => journal.date.startsWith(monthKey) && (journal.blocks.length > 0 || journal.title)).length;
  const monthTone = monthTotal.count === 0 ? {
    background: '#ffffff',
    borderColor: '#eceef1',
    color: '#525866'
  } : monthTotal.resultPct >= 0 ? {
    background: '#edf9f1',
    borderColor: '#63cf96',
    color: '#13b75d'
  } : {
    background: '#fff1ef',
    borderColor: '#f1978e',
    color: '#ef5d52'
  };
  return <section style={{
    width: '100%',
    maxWidth: 'none',
    minWidth: 0,
    padding: '20px 14px 14px'
  }} className="flex flex-col" data-id="element-51" data-mp="3oq7svogj7r9">
      <header className="mb-3 flex flex-wrap items-center justify-between gap-3" data-id="element-52" data-mp="3uvsxfiond5zd">
        <div className="flex items-center gap-5" data-id="element-53" data-mp="irzwv5a6xtpf">
          <div className="flex items-center gap-1" data-id="element-54" data-mp="ir8bsoe7zede">
            <NavButton label="Mes anterior" onClick={() => setCursor(c => addMonths(c, -1))} direction="prev" data-id="element-55" data-mp="mstmc33a7gkf" />
            <span className="min-w-[104px] text-center text-[19px] font-medium capitalize tracking-tight" data-id="element-56" data-mp="rjs5rbjkgurw">
              {format(cursor, 'MMMM', {
              locale: es
            })}
            </span>
            <NavButton label="Mes siguiente" onClick={() => setCursor(c => addMonths(c, 1))} direction="next" data-id="element-57" data-mp="3vyjnfanw7w7i" />
          </div>
          <div className="flex items-center gap-1" data-id="element-58" data-mp="3vircuoon9as">
            <NavButton label="Año anterior" onClick={() => setCursor(c => addYears(c, -1))} direction="prev" data-id="element-59" data-mp="8tk6j4izmogz" />
            <span className="min-w-[64px] text-center text-[19px] font-medium tabular-nums tracking-tight" data-id="element-60" data-mp="dsdopa97z3nh">
              {format(cursor, 'yyyy')}
            </span>
            <NavButton label="Año siguiente" onClick={() => setCursor(c => addYears(c, 1))} direction="next" data-id="element-61" data-mp="1katnnken94f4" />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2" data-id="element-62" data-mp="39qe4ffxomcfq">
          <button type="button" onClick={() => setCursor(new Date())} className="h-9 rounded-lg border border-line px-3 text-[13px] font-medium text-ink-soft transition-colors hover:bg-surface hover:text-ink" data-id="element-63" data-mp="39rxpdjua9bnu">
            Hoy
          </button>
          <button type="button" onClick={() => {
          const text = `DZE Journal · ${format(cursor, 'MMMM yyyy', {
            locale: es
          })} · ${formatResult({
            pct: monthTotal.resultPct,
            money: monthTotal.resultMoney
          })} · ${monthTotal.count} operaciones`;
          void navigator.clipboard?.writeText(text).then(() => setShared(true)).catch(() => setShared(false));
        }} className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-line px-3 text-[13px] font-medium text-ink-soft transition-colors hover:bg-surface hover:text-ink" data-id="element-64" data-mp="2uyvu9yri7c4a">
            <Share2Icon className="h-3.5 w-3.5" data-id="element-65" data-mp="a8mudw5usltc" />{shared ? 'Copiado' : 'Compartir'}
          </button>
          <button type="button" onClick={() => onSelect(format(startOfMonth(cursor), 'yyyy-MM-dd'))} className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-line px-3 text-[13px] font-medium text-ink-soft transition-colors hover:bg-surface hover:text-ink" data-id="element-66" data-mp="24ov5oncbwssx">
            <PlusCircleIcon className="h-3.5 w-3.5" data-id="element-67" data-mp="2nhp86hsrmsg9" />Revisión mensual
          </button>
          <div className="flex items-center gap-2" data-id="element-68" data-mp="2sgim2bbyslgy">
            <span className="hidden text-[12px] text-ink-soft sm:inline" data-id="element-69" data-mp="2sf7l0j9wlrtj">Mostrar:</span>
            <div className="flex h-9 items-center rounded-lg border border-line p-0.5" data-id="element-70" data-mp="2efpqrodu9mcs">
              {([{
              key: 'money',
              label: 'Dinero'
            }, {
              key: 'percent',
              label: 'Porcentaje'
            }] as const).map(option => <button key={option.key} type="button" onClick={() => setMode(option.key)} aria-pressed={mode === option.key} className={cn('h-8 rounded-md px-3 text-[13px] font-medium transition-colors', mode === option.key ? 'bg-ink text-white' : 'text-ink-soft hover:text-ink')} data-id="element-71" data-mp="130t0kb5x4r3u">
                  {option.label}
                </button>)}
            </div>
          </div>
        </div>
      </header>

      <div className="mb-3 flex flex-wrap items-center gap-2" data-id="element-72" data-mp="gu8f8exbj6e5">
        <FilterGroup label="Revisión" value={reviewFilter} onChange={setReviewFilter} options={[{
        key: 'all',
        label: 'Todos'
      }, {
        key: 'pending',
        label: 'Pendientes de revisión'
      }, {
        key: 'reviewed',
        label: 'Revisados'
      }, {
        key: 'unmarked',
        label: 'Sin revisar'
      }]} data-id="element-73" data-mp="bu3yqoweubnx" />
        <FilterGroup label="Plan" value={planFilter} onChange={setPlanFilter} options={[{
        key: 'all',
        label: 'Todas'
      }, {
        key: 'followed',
        label: 'Solo siguiendo el plan'
      }, {
        key: 'broken',
        label: 'Solo fuera del plan'
      }]} data-id="element-74" data-mp="3u5yy1w62vnoz" />
      </div>

      <div className="min-w-0 overflow-x-auto pb-1" data-id="element-75" data-mp="3do3f0puvf2xw">
        <div style={{
        minWidth: 1180
      }} data-id="element-76" data-mp="3dmjwstpnmhth">
          <div style={GRID_STYLE} className="mb-1" data-id="element-77" data-mp="3hp6uhb90awyl">
            {[...WEEKDAYS, 'Total'].map(label => <div key={label} className="py-1.5 text-center text-[14px] font-medium text-ink-soft" data-id="element-78" data-mp="3mp1jf9981ch0">
                {label}
              </div>)}
          </div>

          <div className="flex flex-col" style={{
          rowGap: 3
        }} data-id="element-79" data-mp="8ymtgf15c64">
            {weeks.map((week, index) => {
            const weekOps = filteredOperations.filter(op => {
              const date = parseISO(op.date);
              return date >= week[0] && date <= week[6];
            });
            const weekTotal = totalOf(weekOps);
            return <div key={format(week[0], 'yyyy-MM-dd')} style={GRID_STYLE} data-id="element-80" data-mp="e8zmisjk6dk0" data-mpi={index}>
                  {week.map(day => {
                const key = format(day, 'yyyy-MM-dd');
                return <DayCell key={key} day={day.getDate()} inMonth={isSameMonth(day, cursor)} isToday={isToday(day)} summary={summaries[key]} reviewPending={(reviewByDate[key] ?? 'none') === 'pending'} selected={selected === key} onClick={() => onSelect(key)} data-id="element-81" data-mp="1tts6vnkr34rf" />;
              })}
                  <WeekTotalCell weekLabel={`S${index + 1}`} count={weekTotal.count} resultPct={weekTotal.resultPct} resultMoney={weekTotal.resultMoney} data-id="element-82" data-mp="23pv8msbox1du" />
                </div>;
          })}
          </div>

          <div style={{
          height: 52,
          borderRadius: 6,
          marginTop: 14,
          background: monthTone.background,
          borderColor: monthTone.borderColor,
          borderWidth: 1,
          borderStyle: 'solid'
        }} className="flex items-center justify-end gap-2 px-4" data-id="element-83" data-mp="2g3z9oh221ava">
            <span className="text-[13px] text-ink-soft" data-id="element-84" data-mp="2z3d8pden5bsn">Total del mes:</span>
            <span className="text-[15px] font-semibold tabular-nums" style={{
            color: monthTone.color
          }} data-id="element-85" data-mp="2z1ij3lsfcvi8">
              {formatResult({
              pct: monthTotal.resultPct,
              money: monthTotal.resultMoney
            })}
            </span>
            <span className="text-[13px] text-ink-soft" data-id="element-86" data-mp="38uzegu87mio0">
              · {monthTotal.count} {monthTotal.count === 1 ? 'operación' : 'operaciones'}
              {monthJournals > 0 && ` · ${monthJournals} ${monthJournals === 1 ? 'jornada' : 'jornadas'}`}
            </span>
          </div>
        </div>
      </div>
    </section>;
}
function FilterGroup<T extends string>({
  label,
  value,
  options,
  onChange
}: {
  label: string;
  value: T;
  options: {
    key: T;
    label: string;
  }[];
  onChange: (value: T) => void;
}) {
  return <label className="flex items-center gap-1.5" data-id="element-87" data-mp="3ngeouwdl6xzw">
      <span className="text-[12px] text-ink-soft" data-id="element-88" data-mp="3nflms8pkowo3">{label}:</span>
      <select value={value} onChange={event => onChange(event.target.value as T)} className="h-9 rounded-lg border border-line bg-white px-2.5 text-[12.5px] text-ink focus:outline-none focus:ring-2 focus:ring-accent/25" data-id="element-89" data-mp="3rgffdfdymznb">
        {options.map(option => <option key={option.key} value={option.key} data-id="element-90" data-mp="33snrrfmyz06q">{option.label}</option>)}
      </select>
    </label>;
}
function NavButton({
  label,
  onClick,
  direction
}: {
  label: string;
  onClick: () => void;
  direction: 'prev' | 'next';
}) {
  return <button type="button" onClick={onClick} aria-label={label} className="flex h-8 w-8 items-center justify-center rounded-md text-ink-soft transition-colors hover:bg-surface hover:text-ink" data-id="element-91" data-mp="2utanr2hm73te">
      {direction === 'prev' ? <ChevronLeftIcon className="h-4 w-4" data-id="element-92" data-mp="10j3v72ol2186" /> : <ChevronRightIcon className="h-4 w-4" data-id="element-93" data-mp="10i17t4qgygx0" />}
    </button>;
}